import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ProductGridEntity } from '@shared/domain/products/product-grid.entity';
import { ProjectEntity } from '@shared/domain/projects/project.entity';
import { Dictionary, createList, deserialize } from '@tsim/model';
import { orderBy } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface Brand {
  imageUrl: string;
  label: string;
}

export interface Widget {
  url: string;
  image?: string;
  logo?: string;
  title?: string;
  list?: string[];
  description?: string;
}

@Injectable()
export class HomepageService {
  private http = inject(HttpClient);

  getLastProducts(): Observable<ProductGridEntity[]> {
    return this.http.get<Dictionary[]>(`products/last?limit=8`).pipe(
      map(res => res.map(item => deserialize(ProductGridEntity, item))),
      catchError(this.handleError([]))
    );
  }

  getRecProducts(): Observable<ProductGridEntity[]> {
    const params = new HttpParams({
      fromObject: {
        categoryId: '5d41af21c01ad0e56cb75a92',
      },
    });
    return this.http.get<{ items: Dictionary[] }>(`products/grid?${params}`).pipe(
      map(res => res.items.map(item => deserialize(ProductGridEntity, item))),
      map(products => this.randomProducts(products)),
      catchError(this.handleError([]))
    );
  }

  getLastProjects(limit = 3): Observable<{ total: number; items: ProjectEntity[] }> {
    return this.http.get<Dictionary[]>(`public/projects`).pipe(
      map(res => createList(ProjectEntity, res)),
      map(projects => ({
        total: projects.length,
        items: orderBy(projects, ['completionDate'], ['desc']).slice(0, limit),
      })),
      catchError(this.handleError({ total: 0, items: [] }))
    );
  }

  getWidgets(): Observable<Widget[]> {
    return of([
      {
        url: '/products/kotle/5d41af21c01ad0e56cb75a92?b=61446d2303d9d6000db816da',
        logo: '/assets/brands/viaflames.png',
        title: 'Všechny kotle VIAFLAMES máme skladem!!',
        list: [
          'Splňuje emisní třídu 5 dle ČSN EN',
          'Vysoká účinnost až 90,9%',
          'Nízké náklady na vytápění',
          '7 letá záruka na kotlové těleso',
        ],
      },
      {
        url: '/kotlikove-dotace',
        image: '/assets/dotace-na-kotle.jpg',
      },
      {
        url: '/obsah/vse-o-nakupu/doprava-a-platba',
        image: '/assets/zasilkovna-banner.png',
      },
      {
        url: '/kontakty',
        title: 'Aktuální nabídka zboží',
      },
    ]);
  }

  getBrands(): Observable<Brand[]> {
    return of([
      {
        label: 'Viaflames',
        imageUrl: '/assets/brands/viaflames.webp',
      },
      {
        label: 'Brotje',
        imageUrl: '/assets/brands/brotje.webp',
      },
      {
        label: 'OPOP',
        imageUrl: '/assets/brands/opop.webp',
      },
      {
        label: 'De Dietrich',
        imageUrl: '/assets/brands/dedietrich.webp',
      },
      {
        label: 'Regulus',
        imageUrl: '/assets/brands/regulus.webp',
      },
      {
        label: 'Nibe',
        imageUrl: '/assets/brands/nibe.webp',
      },
      {
        label: 'Grundfos',
        imageUrl: '/assets/brands/grundfos.webp',
      },
      {
        label: 'Danfoss',
        imageUrl: '/assets/brands/danfoss.webp',
      },
      {
        label: 'Marolex',
        imageUrl: '/assets/brands/marolex.webp',
      },
      {
        label: 'Toro',
        imageUrl: '/assets/brands/toro.webp',
      },
    ]);
  }

  getPromoLinks(): Observable<Widget[]> {
    return of([
      {
        url: '/i/obsah/sluzby/montaz-a-servis-plynovych-kotlu',
        title: 'Montáže',
        description: 'Profesionální instalace systémů a potrubí pro kotle a tepelná čerpadla.',
        logo: 'construction',
      },
      {
        url: '/i/obsah/sluzby/revize-kotlu',
        title: 'Servis',
        description: 'Pravidelná údržba a rychlé opravy pro spolehlivý provoz vašich zařízení.',
        logo: 'build',
      },
      {
        url: '/p/products/prislusenstvi-kotelen/5d41af21c01ad0e56cb75ab3',
        title: 'Prodej náhradních dílů',
        description: 'Široký sortiment originálních náhradních dílů pro kotle a tepelná čerpadla.',
        logo: 'shopping_bag',
      },
      {
        url: '/p/products/dum-a-zahrada/5d41af21c01ad0e56cb75a90',
        title: 'Zahrada a dům',
        description: 'Nářadí, sekačky, čerpadla a další vybavení pro vaši zahradu a domácnost.',
        logo: 'home',
      },
    ]);
  }

  private randomProducts(products: ProductGridEntity[]) {
    const recProducts = products.filter(item => item.price > 25000);
    const max = recProducts.length - 1;
    const result: ProductGridEntity[] = [];
    for (let i = 1; i <= 8; i++) {
      let random = i * 3 * new Date().getDate();
      while (random > max) random -= max;
      const product = recProducts[random];
      if (product) result.push(product);
    }
    return result;
  }

  protected handleError<T>(result?: T) {
    return (error: HttpErrorResponse): Observable<T> => {
      this.logError(error);
      return of(result) as Observable<T>;
    };
  }

  protected logError(error: Error): void {
    console.error(error);
  }
}
